import React, { useState } from "react";
import { ClipboardList, Globe, Mail, Plus, RotateCcw } from "lucide-react";

import { Group, StatusBadge, TextInput } from "@/shared/components/settings-atoms";
import { Segmented } from "@/shared/components/teams-ui";
import { GhostButton } from "@/shared/components/ui-kit";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Switch } from "@/shared/components/ui/switch";
import { CONDITIONS, Conditions, type FlowQuestion } from "./Conditions";
import { EditorHeader } from "./EditorHeader";
import { LimitRow } from "./LimitRow";
import { useSavedText } from "./useSavedText";
import { WhoLands } from "./WhoLands";
import { ACTIONS, DEFAULT_REFUSAL, money, type Fallback } from "./copy";
import { sameWhen, wireOf } from "./editing";

import type { Rule, RulePatch, RuleWhen } from "../../types";

const ADD: { key: keyof RuleWhen; Icon: typeof Globe }[] = [
  { key: "channels", Icon: Globe },
  { key: "people", Icon: Mail },
  { key: "answer", Icon: ClipboardList },
  { key: "status", Icon: RotateCcw },
];

/* One policy, edited in place: its name, on or off, Delete; who it applies to; what happens
   to them; and who lands on it. Every change is sent as it is made (a field's words when the
   field is left) — nothing waits for a save. A condition just added holds nobody yet, and the
   server takes no empty one: it stays here until it holds someone, then goes. */
export function PolicyEditor({ policy, n, of, agentName, fallback, questions, shadowAbove, starts, onPatch, onDelete }: {
  policy: Rule;
  n: number;
  of: number;
  agentName: string;
  fallback: Fallback;
  questions: FlowQuestion[];
  /** The one policy switched on above it, which takes the people it shadows; null when several could. */
  shadowAbove: string | null;
  /** A condition to start the editor on, empty: a preset's. */
  starts?: keyof RuleWhen;
  onPatch: (body: RulePatch) => void;
  onDelete: () => void;
}) {
  const [when, setWhen] = useState<RuleWhen>(() => (starts ? { ...policy.when, [starts]: started(starts, questions) } : policy.when));
  const name = useSavedText(policy.name, (typed) => {
    // a policy always has a name: emptied, it keeps the one it had
    if (typed.trim()) onPatch({ name: typed.trim() });
    else name.reset();
  });
  const message = useSavedText(policy.message ?? "", (typed) => onPatch({ message: typed.trim() || null }));

  const changeWhen = (next: RuleWhen) => {
    setWhen(next);
    if (!sameWhen(next, policy.when)) onPatch({ when: wireOf(next) });
  };
  const addable = ADD.filter(({ key }) => !when[key]);
  const shadowed = policy.enabled ? policy.matches.shadowed : 0;

  return (
    <div aria-label="Policy" className="flex flex-col">
      <EditorHeader
        n={n}
        kicker={`Policy ${n} of ${of}${policy.enabled ? "" : " · off"}`}
        actions={
          <>
            <Switch aria-label="On" checked={policy.enabled} onCheckedChange={(on) => onPatch({ enabled: on })} />
            <button
              type="button"
              onClick={onDelete}
              className="press shrink-0 h-7 px-2.5 rounded-[9px] text-[12.5px] font-medium whitespace-nowrap text-[var(--danger)] hover:bg-[var(--danger-bg)]"
            >
              Delete
            </button>
          </>
        }
      >
        <input
          aria-label="Policy name"
          placeholder="Name this policy"
          value={name.value}
          onChange={(e) => name.set(e.target.value)}
          onBlur={name.onBlur}
          onKeyDown={name.onKeyDown}
          className="block w-full h-5 p-0 bg-transparent outline-none text-[14px] font-semibold tracking-[-0.01em] text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)]"
        />
      </EditorHeader>

      <Group
        title="Applies to"
        desc="People who match every condition below."
        action={
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={!addable.length}>
              <GhostButton className="disabled:opacity-40"><Plus className="w-[14px] h-[14px]" strokeWidth={2} />Condition</GhostButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[220px]">
              {addable.map(({ key, Icon }) => (
                <DropdownMenuItem key={key} className="text-[13px] gap-2" onSelect={() => changeWhen({ ...when, [key]: started(key, questions) })}>
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.75} /> {CONDITIONS[key].label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        }
      >
        {Object.keys(when).length ? (
          <Conditions when={when} questions={questions} onChange={changeWhen} />
        ) : (
          <div className="rounded-[12px] ring-1 ring-[var(--hairline)] px-3 py-3.5 flex items-start gap-2.5">
            <StatusBadge tone="warn">Matches everyone</StatusBadge>
            <span className="text-[12.5px] leading-[1.5] text-[var(--text-tertiary)]">With no conditions, the policies below this one never apply.</span>
          </div>
        )}
      </Group>

      <Group title="Then" desc="What happens to the people it matches.">
        <Segmented role="radiogroup" label="What happens" value={policy.action} options={ACTIONS} onChange={(action) => onPatch({ action })} />
        <div className="mt-3 rounded-[12px] ring-1 ring-[var(--hairline)] px-3 divide-y divide-[var(--hairline)]">
          {policy.action === "allow" ? (
            <>
              <LimitRow
                field="daily_budget_usd"
                label="Daily budget (USD)"
                placeholder={fallback.budget === null ? "None" : String(fallback.budget)}
                hint={`What ${agentName} may spend a day (UTC) on the people this policy matches, apart from Everyone else's. Empty counts them in Everyone else's, ${money(fallback.budget)}.`}
                value={policy.daily_budget_usd}
                onSave={(v) => onPatch({ daily_budget_usd: v })}
              />
              <LimitRow
                field="message_limit"
                label="Messages per person, per 10 minutes"
                placeholder={String(fallback.msgs)}
                hint={`Counted across every interface together. Empty uses Everyone else's, ${fallback.msgs}.`}
                value={policy.message_limit}
                onSave={(v) => onPatch({ message_limit: v })}
              />
            </>
          ) : (
            <div className="py-3.5 flex flex-col gap-2">
              <div>
                <div className="text-[13.5px] font-medium text-[var(--text-primary)]">What they're told</div>
                <div className="mt-0.5 text-[12.5px] leading-[1.5] text-[var(--text-tertiary)]">
                  Sent once, in the channel they wrote from. {agentName} doesn't answer them after that.
                </div>
              </div>
              <TextInput
                aria-label="What they're told"
                maxLength={500}
                placeholder={DEFAULT_REFUSAL}
                value={message.value}
                onChange={message.set}
                onBlur={message.onBlur}
                onKeyDown={message.onKeyDown}
              />
            </div>
          )}
        </div>
      </Group>

      <WhoLands
        matches={policy.matches}
        off={!policy.enabled}
        agentName={agentName}
        shadow={shadowed ? `${shadowed} more ${shadowed === 1 ? "person matches" : "people match"} but meet${shadowed === 1 ? "s" : ""} ${shadowAbove ?? "a policy above"} first. Move this policy up to reach them.` : null}
      />
    </div>
  );
}

/** A condition as the editor starts it: empty, but for the stage (still onboarding) and the
    flow's first question. */
function started(key: keyof RuleWhen, questions: FlowQuestion[]): RuleWhen[keyof RuleWhen] {
  if (key === "status") return ["onboarding"];
  if (key === "answer") return { key: questions[0]?.key ?? "", value: "" };
  return [];
}
